import { FanOutReport } from '@/designer/FanOutReport';
import { useCallback, useEffect, useState } from 'react';
import {
  App,
  Alert,
  Button,
  Card,
  Descriptions,
  InputNumber,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExperimentOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { listFlows } from '@/api/flows';
import { runSimulation } from '@/api/simulations';
import type { FlowSummary, SimulationMode, SimulationView } from '@/api/types';
import { ulid } from '@/api/ulid';
import { normalizeApiError, isNotAvailable } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { usePageTitle } from '@/components/pageTitle';
import { InfoTip } from '@/help/InfoTip';

const { Paragraph, Text } = Typography;

const MODES: { value: SimulationMode; label: string; hint: string; dataset: string }[] = [
  {
    value: 'MODE_1',
    label: 'Static impact',
    hint: 'change-class + compile + obligation/cost delta (no execution)',
    dataset:
      'Dataset: NONE by design — this analyses the flow definitions themselves (candidate vs champion): what changed, how risky the change class is (C0 cosmetic → C4 obligation-touching), which REG-## obligations appear/disappear, and the per-case cost delta.',
  },
  {
    value: 'MODE_2',
    label: 'Historical replay',
    hint: 'as-of-bounded replay over the retained outbox',
    dataset:
      'Dataset: YOUR hub\'s own retained history — recorded case events are replayed through the candidate version, bounded as-of the window you choose. Real past traffic, no live side effects.',
  },
  {
    value: 'MODE_4',
    label: 'Synthetic cohort',
    hint: 'deterministic synthetic cohort execution',
    dataset:
      'Dataset: a DETERMINISTIC synthetic cohort — generated test applicants (no real PII) run end-to-end through the candidate. Same seed, same result: reproducible evidence.',
  },
  {
    value: 'MODE_3',
    label: 'Shadow (live fork)',
    hint: 'live traffic fork — 422 until the sim substrate is wired',
    dataset:
      'Dataset: a FORK of live traffic running the candidate alongside the champion. Not wired in this deployment — it refuses honestly (422) rather than fake a result.',
  },
];

function classColor(cc?: string): string {
  if (!cc) return 'default';
  if (cc.startsWith('C0') || cc.startsWith('C1')) return 'success';
  if (cc.startsWith('C2')) return 'warning';
  return 'volcano'; // C3/C4 — AI node / obligation change
}

export function SimulationsPage() {
  usePageTitle('Simulations');
  const { message } = App.useApp();
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [flowId, setFlowId] = useState<string | undefined>();
  const [version, setVersion] = useState<number | undefined>();
  const [champion, setChampion] = useState<number | undefined>();
  const [mode, setMode] = useState<SimulationMode>('MODE_1');
  const [running, setRunning] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [result, setResult] = useState<SimulationView | null>(null);
  const [modeNote, setModeNote] = useState<string | null>(null);

  useEffect(() => {
    listFlows()
      .then((f) => {
        setFlows(f);
        setUnavailable(false);
      })
      .catch((e) => {
        const err = normalizeApiError(e);
        if (isNotAvailable(err)) setUnavailable(true);
        else message.error(err.message);
      });
  }, [message]);

  const selected = flows.find((f) => f.id === flowId);

  const onPick = useCallback(
    (id: string) => {
      setFlowId(id);
      setResult(null);
      setModeNote(null);
      const f = flows.find((x) => x.id === id);
      setVersion(f?.latestVersion);
      setChampion(f?.publishedVersion ?? undefined);
    },
    [flows],
  );

  const run = useCallback(async () => {
    if (!flowId || !version) {
      message.warning('Pick a flow and a candidate version');
      return;
    }
    setRunning(true);
    setResult(null);
    setModeNote(null);
    try {
      const view = await runSimulation(
        { flowId, versionId: version, championVersion: champion, mode },
        ulid(),
      );
      setResult(view);
    } catch (e) {
      const err = normalizeApiError(e);
      if (err.code === 'MODE_SUBSTRATE_UNAVAILABLE') {
        setModeNote(
          `${mode} is not wired in this deployment (${err.message}). MODE_1 static impact always runs.`,
        );
      } else if (err.code === 'MODE_NOT_IMPLEMENTED') {
        setModeNote(`${mode}: ${err.message}`);
      } else {
        message.error(err.message);
      }
    } finally {
      setRunning(false);
    }
  }, [flowId, version, champion, mode, message]);

  if (unavailable) {
    return (
      <EmptyState
        icon={<ExperimentOutlined />}
        title="Simulation & what-if"
        description="The product API is not enabled in this deployment."
        bullets={[]}
      />
    );
  }

  const report = result?.report;
  const staticSection = report?.staticSection;
  type ChangeRow = { type?: string; locus?: string; detail?: string; changeClass?: string };
  const changeCols: ColumnsType<ChangeRow> = [
    { title: 'Type', dataIndex: 'type', key: 'type', width: 160 },
    { title: 'Locus', dataIndex: 'locus', key: 'locus', width: 180, render: (v) => <Text className="ol-mono" style={{ fontSize: 11.5 }}>{v}</Text> },
    { title: 'Detail', dataIndex: 'detail', key: 'detail' },
    {
      title: 'Class',
      dataIndex: 'changeClass',
      key: 'changeClass',
      width: 130,
      render: (cc?: string) => <Tag bordered={false} color={classColor(cc)}>{cc}</Tag>,
    },
  ];

  return (
    <div className="ol-page">
      <div style={{ maxWidth: 1100 }}>
      <PageHeader
        title="Simulations"
        icon={<ExperimentOutlined />}
        subtitle="Impact analysis of a candidate flow version before it goes live. MODE_1 is a static analysis (change class, compile status, obligation/cost delta) and runs in seconds; MODE_2/4 execute against real substrate; MODE_3 (shadow) refuses honestly until the live fork is wired. Every run seals a reproducible, content-addressed ImpactReport."
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="end">
          <div>
            <div style={{ fontSize: 12, color: 'var(--ol-text-tertiary)' }}>
              Flow <InfoTip surface="simulations" field="Flow" />
            </div>
            <Select
              showSearch
              placeholder="Search by name or id…"
              style={{ width: 460 }}
              value={flowId}
              onChange={onPick}
              // Search matches the NAME and the ID; the option renders both so 220 flows
              // stay recognisable (owner feedback 2026-08-09: "no way to recognise the flows").
              filterOption={(input, option) =>
                String(option?.title ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={flows.map((f) => ({
                value: f.id,
                title: `${f.name} ${f.id}`,
                label: (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
                    <span className="ol-mono" style={{ fontSize: 11, color: 'var(--ol-text-tertiary)' }}>
                      {f.id}
                    </span>
                    {f.publishedVersion != null ? (
                      <Tag color="success" bordered={false} style={{ fontSize: 10, marginInlineStart: 'auto' }}>
                        published v{f.publishedVersion}
                      </Tag>
                    ) : (
                      <Tag bordered={false} style={{ fontSize: 10, marginInlineStart: 'auto' }}>
                        draft v{f.latestVersion}
                      </Tag>
                    )}
                  </span>
                ),
              }))}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--ol-text-tertiary)' }}>
              Candidate version <InfoTip surface="simulations" field="Candidate version" />
            </div>
            <InputNumber min={1} value={version} onChange={(v) => setVersion(v ?? undefined)} style={{ width: 140 }} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--ol-text-tertiary)' }}>
              Champion (baseline) <InfoTip surface="simulations" field="Champion (baseline)" />
            </div>
            <InputNumber
              min={1}
              value={champion}
              placeholder={selected?.publishedVersion ? String(selected.publishedVersion) : 'none'}
              onChange={(v) => setChampion(v ?? undefined)}
              style={{ width: 140 }}
            />
          </div>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            loading={running}
            disabled={!flowId || !version}
            onClick={run}
          >
            Run simulation
          </Button>
        </Space>
        <div style={{ marginTop: 12 }}>
          <Segmented
            options={MODES.map((m) => ({ value: m.value, label: m.label }))}
            value={mode}
            onChange={(v) => setMode(v as SimulationMode)}
          />
          <InfoTip surface="simulations" field="Mode" />
          <Text style={{ marginLeft: 12, fontSize: 12, color: 'var(--ol-text-tertiary)' }}>
            {MODES.find((m) => m.value === mode)?.hint}
          </Text>
        </div>
        <Paragraph
          style={{
            marginTop: 10,
            marginBottom: 0,
            fontSize: 12.5,
            color: 'var(--ol-text-secondary)',
            maxWidth: 860,
          }}
        >
          {MODES.find((m) => m.value === mode)?.dataset}
        </Paragraph>
      </Card>

      {modeNote && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={modeNote} />
      )}

      {result && (
        <Card
          size="small"
          title={
            <Space>
              <span>Impact report</span>
              <Tag bordered={false} color={classColor(result.changeClass)}>{result.changeClass}</Tag>
              {result.compiles ? (
                <Tag bordered={false} color="success">compiles</Tag>
              ) : (
                <Tag bordered={false} color="error">does not compile</Tag>
              )}
              {result.blocking && <Tag bordered={false} color="volcano">blocking for regulated</Tag>}
            </Space>
          }
        >
          <Descriptions size="small" column={2} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Mode">{result.mode}</Descriptions.Item>
            <Descriptions.Item label="Report hash">
              <Text className="ol-mono" style={{ fontSize: 11 }}>{result.reportHash}</Text>
            </Descriptions.Item>
            {report?.header?.fidelityGrade && (
              <Descriptions.Item label="Fidelity">{report.header.fidelityGrade}</Descriptions.Item>
            )}
            {report?.header?.containsNondeterministicNodes != null && (
              <Descriptions.Item label="Non-deterministic nodes">
                {report.header.containsNondeterministicNodes ? 'yes' : 'no'}
              </Descriptions.Item>
            )}
          </Descriptions>

          {staticSection?.candidateErrors && staticSection.candidateErrors.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="Candidate compile errors"
              description={<ul style={{ margin: 0, paddingLeft: 18 }}>{staticSection.candidateErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
            />
          )}
          {staticSection?.candidateWarnings && staticSection.candidateWarnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Candidate warnings"
              description={<ul style={{ margin: 0, paddingLeft: 18 }}>{staticSection.candidateWarnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
            />
          )}

          <FanOutReport fanOuts={staticSection?.fanOuts} />

          {staticSection?.changes && staticSection.changes.length > 0 ? (
            <Table
              size="small"
              rowKey={(_, i) => String(i)}
              columns={changeCols}
              dataSource={staticSection.changes}
              pagination={false}
            />
          ) : (
            <Text style={{ color: 'var(--ol-text-tertiary)' }}>
              No structural changes vs the baseline (semantic no-op or first version).
            </Text>
          )}
        </Card>
      )}
      </div>
    </div>
  );
}
